<template>
  <div
    ref="elementRef"
    v-style:bg="backgroundColors"
    class="parent relative"
    :class="[
      parentHoverClasses,
      {
        'overflow-hidden': parallaxLayer,
        'h-full f-col': fullHeight,
      },
      roundedCornersClasses,
    ]"
  >
    <div
      ref="gradientAreaRef"
      class="relative"
      :class="[
        childHoverClasses,
        {
          'min-h-0 grow': fullHeight,
          'overflow-hidden': parallaxOffset,
        },
      ]"
    >
      <cms-shared-hot-zones v-if="hasHotZones" :media />
      <base-picture
        v-if="badge"
        :alt="badge.alt"
        class="absolute right-0 top-0 m-4 h-14 w-14"
        fit="contain"
        :src="badge.url"
      />
      <div
        :class="{ 'h-full': fullHeight }"
        :style="parallaxOffset ? `transform: scale(${parallaxScale})` : null"
      >
        <component
          :is="parallaxOffset ? LazyBaseParallax : 'div'"
          :class="{ 'h-full': fullHeight }"
          :speed="parallaxSpeed"
        >
          <base-picture
            v-if="responsiveMedia.images"
            v-style:h="naturalHeight && transformHeight(responsiveMedia.images)"
            v-bind="responsiveMedia.images"
            class="w-full "
            :class="{ 'h-full': fullHeight }"
            :fetchpriority
            fit="cover"
            :lazy
            :position="objectPosition"
            :props-img="{ class: 'o:h-a' }"
          />
          <vf-video
            v-else-if="responsiveMedia.video"
            v-bind="responsiveMedia.video"
            ref="videoRef"
            v-style:h="naturalHeight && transformHeight(responsiveMedia.video)"
            :autoplay="!lazy"
            class="w-full"
            :class="{ 'h-full': fullHeight }"
            :controls
            fit="cover"
            loop
            :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
            :style-controls="controlsStyleOffset"
          />
        </component>
      </div>
      <cms-base-gradient
        v-if="gradientStyle && gradientStyle !== 'off'"
        v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
        :alignment="gradientAlignment"
        :position="gradientPosition"
        :text-area-only-extent
      />
      <base-link
        v-if="mediaLink && !hasHotZones"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
      <cms-base-caption
        v-if="hasCaption"
        v-bind="captionProps"
        :align-with-overlay
        :class="captionAlignmentClasses"
      />
    </div>
    <cms-base-parallax-layer
      class="w-full"
      :class="{ 'absolute-0': parallaxLayer }"
      :config="parallaxLayer"
    >
      <div
        ref="overlayContentRef"
        class="w-full"
        :class="[
          contentLayoutClasses,
          overlayAlignmentClasses,
          containerWrapperClasses,
          { 'pointer-events-none': overlayClickthrough },
        ]"
      >
        <div
          ref="textAreaRef"
          v-style:bg="backdropColors"
          class="relative f-col overflow-hidden"
          :class="[
            brandClasses.container,
            contentAlignmentClasses,
            blurClass,
          ]"
        >
          <cms-base-eyebrow
            v-if="eyebrowText || eyebrowTextHtml || eyebrowLogoResponsive"
            :eyebrow-logo-responsive
            :eyebrow-logo-width-responsive
            :eyebrow-text
            :eyebrow-text-color="eyebrowTextColor || subtitleColor"
            :eyebrow-text-html
            :eyebrow-text-style
            :position="{ sm: responsiveLayout.sm.alignment, md: responsiveLayout.md?.alignment }"
          />

          <div
            v-if="title || subtitle || richText"
            :class="(title && !hideTitle) || subtitle || richText ? ['f-col', brandClasses.textContainer] : 'contents'"
          >
            <component
              :is="titleTag"
              v-if="title"
              v-style:c="titleColor"
              :class="[titleStyle, { 'sr-only': hideTitle }]"
              data-test-id="cms-linked-card-title"
            >
              <base-link
                v-if="mediaLink"
                :data-cta="mediaLink.delocalizedTracking"
                :target="mediaLink.targetAttribute"
                :to="mediaLink.url"
                @click="handlePromotionClick('title')"
              >
                <cms-plain-text :content field-name="title" />
              </base-link>
              <template v-else>
                <cms-plain-text :content field-name="title" />
              </template>
            </component>
            <p
              v-if="subtitle"
              v-style:c="subtitleColor"
              :class="subtitleStyle"
              data-test-id="cms-linked-card-text"
            >
              <cms-plain-text :content field-name="subtitle" />
            </p>
            <cms-rich-text
              v-if="richText"
              v-style:c="richTextColor || subtitleColor"
              :class="{ 'pointer-within': !overlayClickthrough }"
              :content="{ richText }"
            />
          </div>

          <cms-shared-cta-group
            v-if="linkType !== 'No-CTA' && targets.length"
            :class="ctaAlignmentClasses"
            :cols
            :cta-type
            :equal-size-small-class="brandClasses.ctaEqualSizeSmall"
            :equal-target-size
            :layout="[brandClasses.ctaContainer, ctaFlexClasses]"
            :multilink-style
            :multilink-title
            :size="sharedButton.size"
            :targets
            :text-link-style="getTextLinkStyles(content)"
            @click="(i, target) => handleCtaClick(`cta${i + 1}`, target?.delocalizedTracking)"
          />
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleColor"
            :class="{ 'pointer-within': !overlayClickthrough }"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
      </div>
    </cms-base-parallax-layer>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type { LinkedCardAlignment, LinkedCardContent, LinkedCardLayout } from '#types/components/cms/linked-card'
import type { Target } from '#types/target'
import { LazyBaseParallax } from '#components'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const {
  content,
  controls = true,
  index,
  isPartOfCollection,
  firstElement,
  parentHeadingLevel
} = defineProps<{
  content: LinkedCardContent
  controls?: boolean
  firstElement?: boolean
  fullHeight?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
  naturalHeight?: boolean
}>()

const {
  anchorSmall,
  anchorLarge,
  backdrop,
  backdropBlur,
  backgroundColor,
  alignWithOverlay,
  badge,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor,
  eyebrowTextStyle,
  gradientBreakpoints,
  gradientGrid,
  gradientStyle,
  gradientTone,
  hideTitle,
  hoverStateDisabled,
  linkType,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  overlayClickthrough,
  overlayWidth,
  parallax,
  promotionTracking,
  responsiveLayout,
  richText,
  richTextColor,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [] as Target[],
  title,
  titleColor,
  titleStyle,
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const { $gtm, $feature } = useNuxtApp()
const appConfig = useAppConfig()
const cms = appConfig.components.cms
const { enableTextAreaOnlyGradient } = cms.gradient
const isTextAreaOnlyGradient = isTextAreaOnlyGradientEnabled(enableTextAreaOnlyGradient, gradientStyle)
const gradientAreaRef = useTemplateRef('gradientAreaRef')
const textAreaRef = useTemplateRef('textAreaRef')
const { height: gradientAreaHeight } = useElementSize(gradientAreaRef)
const { height: textAreaHeight } = useElementSize(textAreaRef)
const { top: gradientAreaTop } = useElementBounding(gradientAreaRef)
const { top: textAreaTop } = useElementBounding(textAreaRef)
const { linkedCard: { brandClasses }, sharedButton } = cms
const { componentCorners } = cms.section
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, title || name)
const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const { $viewport } = useNuxtApp()
const parallaxLayer = useParallaxTextLayer(parallax?.config.textLayer)
const videoRef = ref()
const overlayContentRef = ref<HTMLElement>()
const overlayContentHeight = ref(0)
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getResponsiveMedia(media)
const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)

const isOverlayBottom = computed(() =>
  getValueForBreakpoint($viewport.breakpoint, responsiveLayout)?.layout === 'overlay-bottom'
  || getValueForBreakpoint($viewport.breakpoint, responsiveLayout)?.layout === 'overlay'
)

const controlsStyleOffset = computed(() => {
  if (!isOverlayBottom.value || !overlayContentHeight.value) return undefined
  // only offset when content is right-aligned (actual overlap with right-side button)
  const alignment = getValueForBreakpoint($viewport.breakpoint, responsiveLayout)?.alignment
  if (alignment === 'right') return { transform: `translateY(-${overlayContentHeight.value}px)` }
})

useResizeObserver(overlayContentRef, (entries) => {
  const target = entries[0]?.target as HTMLElement
  overlayContentHeight.value = target?.offsetHeight ?? 0
})

const handleCtaClick = (suffix, delocalizedTracking) => {
  handlePromotionClick(suffix)
  if (delocalizedTracking)
    $gtm.push('linkedCard.onClick', delocalizedTracking)
}
const parallaxScaleMap = {
  0: '1',
  1: '1.05',
  2: '1.1',
  3: '1.15',
  4: '1.2',
  5: '1.25',
}

const parallaxOffsetMap = {
  0: 0,
  1: 5,
  2: 10,
  3: 13,
  4: 16,
  5: 20,
}

const parallaxOffset = parallax?.config.innerOffset
const parallaxSpeed = getParallaxSpeed(parallaxOffset, parallaxOffsetMap)
const parallaxScale = parallaxOffset ? parallaxScaleMap[Math.abs(parallaxOffset)] : null

const contentLayout: Partial<Record<LinkedCardLayout, Responsive>> = {
  'overlay': { sm: '<md:absolute <md:z-1 <md:bottom-0 <md:left-0', md: 'md:absolute md:z-1 md:bottom-0 md:left-0' },
  'overlay-bottom': { sm: '<md:absolute <md:z-1 <md:bottom-0 <md:left-0', md: 'md:absolute md:z-1 md:bottom-0 md:left-0' },
  'overlay-top': { sm: '<md:absolute <md:z-1 <md:top-0 <md:left-0', md: 'md:absolute md:z-1 md:top-0 md:left-0' },
  'overlay-middle': { sm: '<md:absolute <md:z-1 <md:top-1/2 <md:-translate-y-1/2', md: 'md:absolute md:z-1 md:top-1/2 md:-translate-y-1/2' }
}

const contentAlignment: Partial<Record<LinkedCardAlignment, Responsive>> = {
  left: { sm: '<md:justify-start <md:text-left', md: 'md:justify-start md:text-left' },
  center: { sm: '<md:items-center <md:text-center', md: 'md:items-center md:text-center' },
  right: { sm: '<md:items-end <md:text-right', md: 'md:items-end md:text-right' }
}

const ctaAlignmentSmall: Record<LinkedCardAlignment, string> = {
  left: $feature.cmsLinkedCardButtonsHorizontal ? '<md:justify-start' : '<md:items-start',
  center: $feature.cmsLinkedCardButtonsHorizontal ? '<md:justify-center' : '<md:items-center',
  right: $feature.cmsLinkedCardButtonsHorizontal ? '<md:justify-end' : '<md:items-end'
}

const ctaAlignmentMedium: Record<LinkedCardAlignment, string> = {
  left: 'md:justify-start',
  center: 'md:justify-center',
  right: 'md:justify-end',
}

const contentLayoutClasses = [
  contentLayout[responsiveLayout.sm.layout]?.sm,
  responsiveLayout.md?.layout && contentLayout[responsiveLayout.md.layout]?.md,
].filter(Boolean).join(' ')

const contentAlignmentClasses = [
  contentAlignment[responsiveLayout.sm.alignment]?.sm,
  responsiveLayout.md?.alignment && contentAlignment[responsiveLayout.md.alignment]?.md,
].filter(Boolean).join(' ')

const ctaAlignmentClasses = [
  ctaAlignmentSmall[responsiveLayout.sm.alignment],
  responsiveLayout.md?.alignment && ctaAlignmentMedium[responsiveLayout.md.alignment],
].filter(Boolean).join(' ')

const gradientDirection = {
  'overlay-top': 'top',
  'overlay-bottom': 'bottom',
  'overlay-middle': 'center'
}

const getProjectedGradientPosition = (
  pos?: 'top' | 'bottom' | 'center'
): 'top' | 'bottom' => {
  if (pos !== 'center')
    return pos || 'top'

  if (
    gradientAreaHeight.value
    && textAreaHeight.value
    && gradientAreaTop.value !== undefined
    && textAreaTop.value !== undefined
  ) {
    const textAreaCenter = textAreaTop.value + (textAreaHeight.value / 2)
    const gradientAreaCenter = gradientAreaTop.value + (gradientAreaHeight.value / 2)

    return textAreaCenter > gradientAreaCenter ? 'bottom' : 'top'
  }

  return 'top'
}

const gradientPosition = computed<Responsive<'top' | 'bottom'>>(() => ({
  sm: getProjectedGradientPosition(gradientDirection[responsiveLayout.sm.layout]),
  md: responsiveLayout.md?.layout
    ? getProjectedGradientPosition(gradientDirection[responsiveLayout.md.layout])
    : undefined,
}))

const gradientAlignment = {
  sm: responsiveLayout.sm.alignment,
  md: responsiveLayout.md?.alignment
}

const getTextAreaOnlyExtent = (
  pos?: 'top' | 'bottom' | 'center',
) => {
  const fallbackExtent = pos === 'center' ? '67%' : '33%'
  if (!textAreaHeight.value)
    return fallbackExtent

  const extent = pos === 'bottom'
    ? gradientAreaHeight.value && textAreaTop.value && gradientAreaTop.value
      ? gradientAreaHeight.value - (textAreaTop.value - gradientAreaTop.value)
      : textAreaHeight.value
    : pos === 'center'
      ? gradientAreaHeight.value
        ? (gradientAreaHeight.value + textAreaHeight.value) / 2
        : undefined
      : textAreaHeight.value

  if (!extent || extent <= 0)
    return fallbackExtent

  return `${Math.ceil(extent)}px`
}

const textAreaOnlyExtent = computed<Responsive<string> | undefined>(() => {
  if (!isTextAreaOnlyGradient)
    return undefined

  const smallExtent = getTextAreaOnlyExtent(gradientPosition.value.sm)
  const mediumExtent = getTextAreaOnlyExtent(gradientPosition.value.md)
  const resolvedExtent = smallExtent || mediumExtent

  if (!resolvedExtent)
    return undefined

  return {
    sm: resolvedExtent,
    ...(mediumExtent && { md: mediumExtent }),
    lg: mediumExtent || resolvedExtent,
  }
})

const isOverlayLayout: Responsive<boolean | undefined> = {
  sm: getValueForBreakpoint('sm', responsiveLayout)?.layout?.includes('overlay'),
  md: getValueForBreakpoint('md', responsiveLayout)?.layout?.includes('overlay'),
  lg: getValueForBreakpoint('lg', responsiveLayout)?.layout?.includes('overlay')
}

const containerWrapperClasses = (Object.keys(isOverlayLayout) as (keyof Responsive)[])
  .map((bp) => isOverlayLayout[bp] && getValueForBreakpoint(bp, brandClasses.containerWrapper))
  .filter(Boolean)
  .join(' ')

const captionAlignmentClasses = alignWithOverlay
  ? [containerWrapperClasses, brandClasses.container]
  : undefined

const hasHotZones = computed(() => Object.values(media || []).some((media) => media.enableImageMap))

const parentHoverClasses = computed(() => ({
        '<md:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.sm && !hasHotZones.value,
  '~md:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.md && !hasHotZones.value,
  'lg:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.lg && !hasHotZones.value,
}))

const childHoverClasses = computed(() => (!hoverStateDisabled && {
        '<md:duration <md:parent-hover:rotate-2 <md:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.sm && !hasHotZones.value,
  '~md:duration ~md:parent-hover:rotate-2 ~md:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.md && !hasHotZones.value,
  'lg:duration lg:parent-hover:rotate-2 lg:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.lg && !hasHotZones.value,
}))

const cols = isPartOfCollection
  ? 'repeat(1,minmax(0,1fr))'
  : targets.map((target) => target.style?.sm === 'text-link' ? 'max-content' : 'minmax(0,1fr)').join(' ')

const { backgroundColors, backdropColors } = Object.entries(isOverlayLayout).reduce((acc, [cur, isOverlay]) => ({
  backgroundColors: { ...acc.backgroundColors, [cur]: isOverlay ? 'transparent' : getValueForBreakpoint(cur as keyof Responsive, backgroundColor) },
  backdropColors: { ...acc.backdropColors, [cur]: isOverlay ? getValueForBreakpoint(cur as keyof Responsive, backdrop) : 'transparent' },
}), {
  backgroundColors: {} as Record<keyof Responsive, string>,
  backdropColors: {} as Record<keyof Responsive, string>,
})

const titleTag = computed(() => isPartOfCollection
  ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
  : firstElement ? 'h1' : 'h2')

const overlaySize = {
  sm: {
    xs: '<md:w-10/12',
    sm: '<md:w-10/12',
    md: '<md:w-10/12',
    lg: '<md:w-full'
  },
  md: {
    xs: 'md:w-1/3',
    sm: 'md:w-1/2',
    md: 'md:w-2/3',
    lg: 'md:w-11/12'
  }
}

const overlayAlignment = {
  center: {
    sm: '<md:left-1/2 <md:-translate-x-1/2',
    md: 'md:left-1/2 md:-translate-x-1/2'
  },
  right: {
    sm: '<md:right-0 <md:left-a',
    md: 'md:right-0 md:left-a'
  }
}

const overlayAlignmentClasses = overlayWidth && ['sm', 'md']
  .map((bp) => responsiveLayout[bp].layout.includes('overlay') && [overlaySize[bp][overlayWidth], overlayAlignment[responsiveLayout[bp].alignment]?.[bp]])

const { hasCaption, captionProps } = getCaptionConfig(content)

const transformHeight = (media) => Object.fromEntries(Object.entries(media.height).map(([k, v]) => [k, `${v}px`]))

const roundedCornersClasses = getRoundedCorners(content, componentCorners, { force: isPartOfCollection })

const ctaFlexClasses = $feature.cmsLinkedCardButtonsHorizontal
  ? brandClasses.ctaContainerHorizontal
  : brandClasses.ctaContainerVertical

const blurClass = backdropBlur && (backdropBlur === 'low' ? 'backdrop-blur-xs' : 'backdrop-blur-md')

useAutoplay(videoRef)
</script>
